import {
  Link as RouterLink,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Alert, Box, Button, List, Stack, Typography } from "@mui/material";
import { useAuth, useCoachingRequest } from "@bw/shared/hooks";
import { Page } from "../../../common/components/Page";
import { LessonRow } from "../components/LessonRow";
import { CoachingProposalPanel } from "../components/CoachingProposalPanel";
import { coachingUuid } from "../utils/coachingSelection";

export function CoachingRequestPage() {
  const { requestId = "" } = useParams(),
    { user, loading } = useAuth();
  const [params] = useSearchParams();
  const location = useLocation();
  const revisions = params.getAll("revision_id");
  const selected = revisions.length > 0;
  const revisionId = revisions.length === 1 ? coachingUuid(revisions[0]) : null;
  const canonicalRequestId = coachingUuid(requestId);
  const incompatible = [
    "tab",
    "acceptance_id",
    "lessons_page",
    "payments_page",
  ].some((name) => params.has(name));
  const allParams = new URLSearchParams(params);
  for (const name of [
    "revision_id",
    "tab",
    "acceptance_id",
    "lessons_page",
    "payments_page",
  ])
    allParams.delete(name);
  const allRequest = `${location.pathname}${allParams.size ? `?${allParams}` : ""}${location.hash}`;
  return (
    <Page title="Coaching request">
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
        <Button component={RouterLink} to="/me/lessons" size="small">
          Back to my lessons
        </Button>
        {selected || incompatible ? (
          <Button component={RouterLink} to={allRequest} size="small">
            View coaching request
          </Button>
        ) : null}
      </Stack>
      {loading || !user ? (
        <Typography role="status">
          Sign in to view this coaching request.
        </Typography>
      ) : incompatible ? (
        <Alert severity="info">This coaching record is unavailable.</Alert>
      ) : selected ? (
        revisionId && canonicalRequestId ? (
          <CoachingProposalPanel
            key={`${user.uid}:${canonicalRequestId}:${revisionId}`}
            requestId={canonicalRequestId}
            revisionId={revisionId}
          />
        ) : (
          <Alert severity="info">This proposal is unavailable.</Alert>
        )
      ) : (
        <RequestContent
          key={`${user.uid}:${canonicalRequestId ?? requestId}`}
          requestId={canonicalRequestId ?? requestId}
        />
      )}
    </Page>
  );
}

function RequestContent({ requestId }: { requestId: string }) {
  const query = useCoachingRequest(requestId);
  const item = query.retainedData;
  const matches = item?.request_id?.toLowerCase() === requestId.toLowerCase();
  const current = query.isCurrent && matches;
  return (
    <>
      {query.fetchStatus === "paused" ? (
        <Alert severity="info" role="status">
          Waiting for a connection to refresh your coaching request.
        </Alert>
      ) : query.isError || (query.isCurrent && !matches) ? (
        <Alert severity="warning">
          This coaching request is unavailable. Your access may have changed.
        </Alert>
      ) : !current ? (
        <Typography role="status">Loading coaching request…</Typography>
      ) : null}
      {item && matches ? (
        <Box hidden={!current} inert={!current}>
          <List disablePadding>
            <LessonRow key={requestId} item={item} />
          </List>
        </Box>
      ) : null}
    </>
  );
}
